import React, { Suspense } from "react";
import { useUserLoggedInStatus, useUserRole } from "../../../shared/state/user";

import { matchPath, Navigate, RouteProps, useLocation } from "react-router-dom";
import { COREError } from "../../../COREDesignSystem/Content/COREError";
import { CORELoading } from "../../../COREDesignSystem/Feedback/CORELoading";
import { PageLayout } from "./PageLayout";
import { Empty } from "antd";
import {
  CURRENT_VERSION,
  fetchVersionFromIndexHtml,
  isVersionValid,
  useCheckFEVersionHasChanged,
} from "../../../shared/useCheckFEVersionHasChanged";
import { usePageChange } from "../../../shared/usePageChange";
import { useUserAccessibleSections } from "../../../shared/customHoooks/useUserAccessibleSections";
import { useUserDashboard } from "../../../shared/customHoooks/useUserDashboard";
import {
  FeatureFlag,
  useFeatureFlags,
} from "../../../shared/customHoooks/useFeatureFlags";
import { UserAccessibleSection } from "../../../openapi-typescript/common/user_accessible_section";
import { DEFAULT_HOMEPAGE_URL } from "../../../shared/global";
import { routesWithParams } from "../../../COREDesignSystem/Layout/COREPageHeader";
import {
  getGenericPreviewLogic,
  PREVIEW_MAPPINGS,
} from "../../markets/preview/marketPreviewHelper";
import {
  FeatureCard,
  useFeatureCards,
} from "../../../shared/customHoooks/useFeatureCards";

const previewFlags = PREVIEW_MAPPINGS.map(({ previewFlag }) => previewFlag);

const isWithinPath = (pathname: string, basePath: string) =>
  pathname === basePath || pathname.startsWith(`${basePath}/`);

// The page belongs to the card(s) with the longest URL containing it.
const getPageCards = (featureCards: FeatureCard[], pathname: string) => {
  const containing = featureCards.filter(
    ({ callToActionUrl }) =>
      callToActionUrl?.startsWith("/") &&
      isWithinPath(pathname, callToActionUrl)
  );
  const longest = Math.max(
    ...containing.map(({ callToActionUrl }) => callToActionUrl.length)
  );
  return containing.filter(
    ({ callToActionUrl }) => callToActionUrl.length === longest
  );
};

export type PageLayoutRouteProps = Pick<RouteProps, "element"> & {
  path: Exclude<RouteProps["path"], undefined | readonly string[]>;
};

const noPermissionMessage = "No permission to view this page.";
const redirectToLogin = (fullPath: string) => {
  const to =
    Boolean(fullPath) && fullPath !== "/"
      ? `/login?next=${encodeURIComponent(fullPath)}`
      : "/login";
  return <Navigate to={to} />;
};

export const useCheckUserAccess = (
  path?: PageLayoutRouteProps["path"] | null
) => {
  const pageListForCheckFeatureFlag: {
    page: PageLayoutRouteProps["path"];
    featureFlag: FeatureFlag | FeatureFlag[];
  }[] = [
    {
      page: "/todo",
      featureFlag: "todo-list",
    },
  ];
  const currentPage = pageListForCheckFeatureFlag.find((p) => p.page === path);
  const {
    loading: loadingUserAccessibleSections,
    sync: syncUserAccessibleSections,
    error: errorUserAccessibleSections,
    userAccessibleSections,
  } = useUserAccessibleSections();
  const {
    loading: loadingMenuPermission,
    sync: syncMenuPermission,
    error: errorMenuPermission,
    hasFeature,
    hasAnyFeature,
    features,
  } = useFeatureFlags();
  const {
    loading: loadingFeatureCards,
    sync: syncFeatureCards,
    error: errorFeatureCards,
    featureCards,
  } = useFeatureCards();
  const hasPermissionByFeatureFlag = currentPage
    ? Array.isArray(currentPage.featureFlag)
      ? hasAnyFeature(currentPage.featureFlag)
      : hasFeature(currentPage.featureFlag)
    : false;

  const ignorePages = [
    "/website-terms-of-use",
    "/privacy-policy",
    "/terms-and-conditions",
    "/help",
    // Legacy Daily Curves URLs that only render a client-side redirect to
    // their new Benchmarks equivalent; no content is exposed, so they don't
    // need feature-flag gating of their own.
    "/markets/daily-curves/carbon/:date?",
    "/markets/daily-curves/environmental/:date?",
    "/markets/daily-curves/electricity/:date?",
    "/markets/daily-curves/fixed-power-shapes/:date?",
    "/markets/daily-curves/hourly-power-shapes/:date?",
    "/markets/daily-curves",
    // Legacy Asset Intelligence Benchmarks & Commentary URL, now redirects
    // to its Market Dynamics equivalent.
    "/asset-intelligence/benchmarks-commentary",
    // Legacy Markets Admin market prices URLs, consolidated into Power.
    "/markets-admin/market-prices/electricity",
    "/markets-admin/market-prices/electricity-shapes",
  ];

  // Specific case: Pages that inherit permission from their parent section
  const parentPathPermissions: {
    childPath: string;
    parentPath: string;
  }[] = [
    {
      childPath: "/asset-intelligence/admin-centre/add-asset",
      parentPath: "/asset-intelligence/admin-centre",
    },
    {
      childPath: "/asset-intelligence/admin-centre/:id/edit",
      parentPath: "/asset-intelligence/admin-centre",
    },
  ];

  const locationPathName = window.location.pathname;
  const pagePath =
    path && matchPath({ path }, locationPathName) ? locationPathName : path;

  // A preview-entitled user has to reach the page for the preview to render:
  // userAccessibleSections only lists pages they fully own.
  const hasPermissionByPreview = pagePath
    ? getGenericPreviewLogic(pagePath, features).shouldShowPreview
    : false;

  // A section, preview or parent flag never opens a page whose own flag the
  // company lacks; preview flags only count where the page shows the preview.
  const pageCards = pagePath ? getPageCards(featureCards, pagePath) : [];
  const hasPageFeature =
    !pageCards.length ||
    hasPermissionByPreview ||
    pageCards.some(
      ({ featureFlag }) =>
        !previewFlags.includes(featureFlag) && hasFeature(featureFlag)
    );

  const hasPermissionByUserAccessibleSection = (
    sections: UserAccessibleSection[]
  ): boolean => {
    return sections.some((section) => {
      const { callToActionUrl, children } = section;
      const isDirectMatch =
        path === callToActionUrl || locationPathName === callToActionUrl;
      const isSubPathMatch =
        locationPathName.startsWith(`${callToActionUrl}/`) && !children?.length;

      const routewithParams =
        path &&
        routesWithParams.find((route) => matchPath({ path: route }, path));

      const hasChildPermission = children?.length
        ? hasPermissionByUserAccessibleSection(children)
        : false;

      const isParentPathMatch = parentPathPermissions.some(
        ({ childPath, parentPath }) =>
          path === childPath && callToActionUrl === parentPath
      );

      return (
        isDirectMatch ||
        isSubPathMatch ||
        routewithParams ||
        hasChildPermission ||
        isParentPathMatch
      );
    });
  };

  return {
    loading:
      loadingUserAccessibleSections ||
      loadingMenuPermission ||
      loadingFeatureCards,
    sync: syncUserAccessibleSections && syncMenuPermission && syncFeatureCards,
    error:
      errorUserAccessibleSections || errorMenuPermission || errorFeatureCards,
    hasPermission: path
      ? ignorePages.includes(path) ||
        (hasPageFeature &&
          (hasPermissionByUserAccessibleSection(userAccessibleSections) ||
            hasPermissionByFeatureFlag ||
            hasPermissionByPreview ||
            path.includes("/dashboard")))
      : false,
  };
};

export const PageLayoutRoute: React.FC<PageLayoutRouteProps> = ({
  path,
  element,
}) => {
  const location = useLocation();
  const isPendingRole = useUserRole() === "pending";
  const isLoggedIn = useUserLoggedInStatus();
  const {
    sync: syncDashboard,
    loading: loadingDashboard,
    error: errorDashboard,
    markAsHomepage,
    configs: { homepage },
  } = useUserDashboard();
  const hasETAGChanged = useCheckFEVersionHasChanged();
  const { loading, sync, error, hasPermission } = useCheckUserAccess(path);
  const { hasPermission: isHomepageAllow } = useCheckUserAccess(homepage);

  usePageChange(async () => {
    if (hasETAGChanged) {
      window.location.reload();
      return;
    }
    const version = await fetchVersionFromIndexHtml();
    if (
      version !== CURRENT_VERSION &&
      isVersionValid(CURRENT_VERSION) &&
      isVersionValid(version)
    ) {
      window.location.reload();
    }
  });
  const { pathname, search, hash } = location;

  if (!isLoggedIn) {
    return redirectToLogin(pathname + search + hash);
  }

  if (isPendingRole) {
    return (
      <PageLayout>
        <COREError
          title={
            "No permission to view this page. This account is still pending."
          }
        />
      </PageLayout>
    );
  }

  switch (true) {
    case (loading && !sync && !error) ||
      (loadingDashboard && !syncDashboard && !errorDashboard):
      return <CORELoading size={"lg"} />;
    case !!error || !!errorDashboard:
      return (
        <COREError
          title={`Error loading feature list. ${
            error &&
            typeof error === "object" &&
            error.hasOwnProperty("message")
              ? error.message
              : ""
          }`}
        />
      );
    case !sync || !syncDashboard:
      return (
        <PageLayout>
          <Empty />
        </PageLayout>
      );
    case location.pathname === "/":
      if (homepage && isHomepageAllow) {
        return <Navigate to={homepage} replace={true} />;
      } else {
        markAsHomepage(DEFAULT_HOMEPAGE_URL);
        return <Navigate to={DEFAULT_HOMEPAGE_URL} replace={true} />;
      }
    case hasPermission:
      return (
        <Suspense fallback={<CORELoading delay={0} />}>{element}</Suspense>
      );
    default: {
      return (
        <PageLayout>
          <COREError
            title={noPermissionMessage}
            path={pathname === homepage ? DEFAULT_HOMEPAGE_URL : "/"}
            onClick={() => {
              if (pathname === homepage) {
                markAsHomepage(DEFAULT_HOMEPAGE_URL);
              }
            }}
          />
        </PageLayout>
      );
    }
  }
};
